import { FormFieldLayoutProps, FormText } from "@itk/mui-components";
import { LoginFlowStateInputResolverProps } from "./login-flow-types";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { handleOnFieldChange } from "@itk/react-form";
import { Box, CircularProgress, Stack, styled, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { DekoxIcon } from "src/components/deko-x-icons";
import { passwordValidationCheckmarks } from "src/utils/password-utils";
import { FormPhone } from "src/components/form-phone";

const Slot = styled(Box, {
  shouldForwardProp: prop => prop !== "isActive"
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  width: "2.5rem",
  height: "2.5rem",
  fontSize: theme.typography.body1.fontSize,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s",
  borderRadius: 2,
  border: `1px solid ${theme.palette.customColors.border}`,
  "&.Mui-focusVisible, &:hover": {
    borderColor: theme.palette.secondary.main
  },
  ...(isActive && {
    outline: `2px solid ${theme.palette.primary.main}`
  })
}));

export const LoginFlowStateInputResolver = (props: LoginFlowStateInputResolverProps) => {
  const { state, form, onclick, loginState } = props;

  const validationMessageTranslationsT = useValidationMessageTranslationsT();
  const genericTranslationsT = useGenericTranslationsT();

  const { formState, formController } = form;
  const theme = useTheme();

  const [isShowingPassword, setIsShowingPassword] = useState(false);

  const width: FormFieldLayoutProps["width"] = "100%";

  const current = useMemo(() => {
    switch (state.current) {
      case "login":
        return (
          <>
            <FormText width={width} formFieldState={formState.email} formFieldController={formController.email} t={genericTranslationsT} field="emailAddress" k={genericTranslationsK} autoFocus />
            <FormText
              width={width}
              formFieldState={formState.password}
              formFieldController={formController.password}
              t={genericTranslationsT}
              field="password"
              k={genericTranslationsK}
              type={isShowingPassword ? "text" : "password"}
              slotProps={{
                input: {
                  endAdornment: <DekoxIcon icon={isShowingPassword ? "visibilityOff" : "removeRedEye"} sx={{ cursor: "pointer" }} onClick={() => setIsShowingPassword(!isShowingPassword)} />
                }
              }}
              onKeyUp={e => {
                if (e.key === "Enter" && onclick) onclick();
              }}
            />
          </>
        );
      case "otp":
        return (
          <Stack direction="row" alignItems="center" gap={2}>
            <OTPInput
              autoFocus
              onChange={e => {
                if (Number(e) > 0) handleOnFieldChange(Number(e), formController.oneTimeKey);
                else handleOnFieldChange(null, formController.oneTimeKey);
              }}
              value={formState.oneTimeKey.value?.toString() ?? ""}
              maxLength={4}
              pattern={REGEXP_ONLY_DIGITS}
              onComplete={() => {
                if (onclick) onclick();
              }}
              disabled={loginState.currentState === "loading"}
              render={({ slots }) => (
                <Stack flexDirection="row" gap={1}>
                  {slots.map((slot, idx) => (
                    <Slot key={idx} isActive={slot.isActive}>
                      {slot.char !== null && slot.char}
                    </Slot>
                  ))}
                </Stack>
              )}
            />
            {loginState.currentState === "loading" && <CircularProgress size={18} />}
          </Stack>
        );
      case "new-password":
        return (
          <Stack gap={2}>
            <FormText
              autoComplete="new-password"
              width={width}
              formFieldState={formState.password}
              formFieldController={formController.password}
              t={genericTranslationsT}
              field="password"
              k={genericTranslationsK}
              type={isShowingPassword ? "text" : "password"}
              onKeyUp={e => {
                if (e.key === "Enter" && onclick) onclick();
              }}
              slotProps={{
                input: {
                  endAdornment: <DekoxIcon icon={isShowingPassword ? "visibilityOff" : "removeRedEye"} sx={{ cursor: "pointer" }} onClick={() => setIsShowingPassword(!isShowingPassword)} />
                }
              }}
            />
            <FormText
              autoComplete="confirm-password"
              width={width}
              formFieldState={formState.confirmPassword}
              formFieldController={formController.confirmPassword}
              t={genericTranslationsT}
              field="confirmPassword"
              k={genericTranslationsK}
              type={isShowingPassword ? "text" : "password"}
              onKeyUp={e => {
                if (e.key === "Enter" && onclick) onclick();
              }}
              slotProps={{
                input: {
                  endAdornment: <DekoxIcon icon={isShowingPassword ? "visibilityOff" : "removeRedEye"} sx={{ cursor: "pointer" }} onClick={() => setIsShowingPassword(!isShowingPassword)} />
                }
              }}
            />
            <Stack gap={0.8}>
              {passwordValidationCheckmarks(formState.password.value || "", validationMessageTranslationsT).map(passwordValidationCheckmark => {
                return (
                  <Stack
                    key={passwordValidationCheckmark.text}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    color={passwordValidationCheckmark.checked ? theme.palette.primary.main : theme.palette.customColors.darkGrey}
                  >
                    <DekoxIcon icon="checkCircle" fontSize="small" color="inherit" />
                    <Typography variant="subtitle1" fontWeight={passwordValidationCheckmark.checked ? "bold" : "normal"}>
                      {passwordValidationCheckmark.text}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        );
      case "profile-info":
        return (
          <Stack direction="column" gap={1}>
            <FormText
              width={width}
              formFieldState={formState.fullName}
              formFieldController={formController.fullName}
              t={genericTranslationsT}
              field="name"
              k={genericTranslationsK}
              onKeyUp={e => {
                if (e.key === "Enter" && onclick) onclick();
              }}
            />
            <FormPhone
              phoneNumberFormFieldController={formController.mobilePhoneNo}
              phoneNumberFormFieldState={formState.mobilePhoneNo}
              phoneNumberTranslation={{ t: genericTranslationsT, field: "mobilePhoneNo", k: genericTranslationsK }}
              prefixFormFieldController={formController.mobilePhoneCountryId}
              prefixFormFieldState={formState.mobilePhoneCountryId}
              prefixTranslation={{ t: genericTranslationsT, field: "mobilePhonePrefix", k: genericTranslationsK }}
              width="flex"
            />
          </Stack>
        );

      case "forgot-password":
        if (state.stage === "code")
          return (
            <Stack direction="row" alignItems="center" gap={2}>
              <OTPInput
                autoFocus
                onChange={e => {
                  if (Number(e) > 0) handleOnFieldChange(Number(e), formController.oneTimeKey);
                  else handleOnFieldChange(null, formController.oneTimeKey);
                }}
                value={formState.oneTimeKey.value?.toString() ?? ""}
                maxLength={4}
                pattern={REGEXP_ONLY_DIGITS}
                disabled={loginState.currentState === "loading"}
                onComplete={() => {
                  if (onclick) onclick();
                }}
                render={({ slots }) => (
                  <Stack flexDirection="row" gap={1}>
                    {slots.map((slot, idx) => (
                      <Slot key={idx} isActive={slot.isActive} contentEditable>
                        {slot.char !== null && slot.char}
                      </Slot>
                    ))}
                  </Stack>
                )}
              />
              {loginState.currentState === "loading" && <CircularProgress size={18} />}
            </Stack>
          );
        return (
          <FormText
            t={genericTranslationsT}
            k={genericTranslationsK}
            field="emailAddress"
            formFieldState={form.formState.email}
            formFieldController={form.formController.email}
            width="100%"
            onKeyUp={e => {
              if (e.key === "Enter" && onclick) onclick();
            }}
          />
        );

      default:
        return <></>;
    }
  }, [formState, formController, state.current, onclick, handleOnFieldChange, isShowingPassword, loginState]);

  return current;
};
